import { createSelector } from 'reselect';
import _reduce from 'lodash/reduce';
import _flatMap from 'lodash/flatMap';
import axios from 'axios';


import { ACCOUNTS_LOADED, getFirstAccount } from 'modules/accounts';
import { SIGN_OUT_SUCCESS, getAuthToken } from 'modules/auth';
import { BILLING_TOKEN_API } from 'config';


/****** ACTION TYPES ********/

export const PAYMENT_METHOD_UPDATE = 'PAYMENT_METHOD_UPDATE';
export const CUSTOMER_ID_UPDATED = 'CUSTOMER_ID_UPDATED';

export const UPDATE_SUBSCRIPTION = 'UPDATE_SUBSCRIPTION';
export const RETRIEVE_BILLING_INFO = 'RETRIEVE_BILLING_INFO';
export const BILLING_INFO_RECEIVED = 'BILLING_INFO_RECEIVED';

export const BILLING_LOADING = 'BILLING_LOADING';


/****** REDUCER  ********/

const defaultState = {
  loaded: false,
  items: [],
  customers: {}
};

export default (state = defaultState, { type, payload, meta }) => {
  switch (type) {
    case ACCOUNTS_LOADED:
      return {
        ...state,
        items: _flatMap(payload, account => {
          return _reduce(account.subscriptions || {}, (acc, value, key) => {
            acc.push({ id: key, planId: value, account: account.key });
            return acc;
          }, [])
        })
      };

    case BILLING_INFO_RECEIVED:
      return {
        ...state,
        items: (state.items || [])
          // update details for the subscriptions loaded from Firebase.
          .map(item => {
            const details = (payload.subscriptions || []).find(s => s.id === item.id);
            return details ? { ...item, ...details } : item;
          })
          // remove missing (deleted) subscriptions
          .filter(item => item.account !== meta.customerId || !payload.subscriptions.find(s => s.id === item.id))
          // add Pending subscriptions, since they are not stored in Firebase.
          .concat((payload.subscriptions || []).reduce((acc, value) => {
            if (!state.items.find(i => i.id === value.id)) {
              acc.push({ ...value, account: meta.accountId });
            }
            return acc;
          }, [])),
        customers: payload.customerId ?
          { ...state.customers || {}, [meta.accountId]: payload.customerId } :
          state.customers
      };

    case CUSTOMER_ID_UPDATED:
      return {
        ...state,
        items: state.items.map(i => i.account === meta.accountId ? { ...i, customerId: payload } : i),
        customers: { ...state.customers || {}, [meta.accountId]: payload },
      };

    case BILLING_LOADING:
      return { ...state, loaded: !payload };

    case SIGN_OUT_SUCCESS:
      return {};

    default:
      return state;
  }
}


/****** ACTION CREATORS ******/

export const getClientToken = (account, cancel) => getAuthToken()
  .then(token =>
    axios.get(
      BILLING_TOKEN_API,
      {
        cancelToken: cancel ? cancel.token : undefined,
        params: { account },
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + token,
        }
      }
    )
  )
  .then(response => response.data.body || '');

export const billingLoading = isLoading => ({ type: BILLING_LOADING, payload: isLoading });

export const updateSubscription = (customerId, planId) => {
  return { type: UPDATE_SUBSCRIPTION, payload: { planId, customerId } };
};

export const updatePaymentMethod = (customerId, nonce) => {
  return {
    type: PAYMENT_METHOD_UPDATE,
    payload: { nonce },
    meta: { customerId }
  }
};

export const updateCustomerId = (customerId) => {
  return { type: CUSTOMER_ID_UPDATED, payload: customerId, meta: { accountId: customerId } };
};

export const retrieveBillingInfo = accountId => ({ type: RETRIEVE_BILLING_INFO, payload: accountId })

export const saveBillingInfo = (subscription, accountId) => ({
  type: BILLING_INFO_RECEIVED,
  payload: subscription,
  meta: { accountId }
});



/****** SELECTORS *******/

const getSubscriptions = state => state.subscriptions.items;
const getCustomers = state => state.subscriptions.customers;

export const getActiveSubscription = createSelector(
  getFirstAccount,
  getSubscriptions,
  (account, subscriptions) => account ? subscriptions.find(s => s.account === account.key && (typeof s.status === 'undefined' || s.status === 'Active')) : null
);

export const getNextSubscription = createSelector(
  getFirstAccount,
  getSubscriptions,
  (account, subscriptions) => account ? subscriptions.find(s => s.account === account.key && s.neverExpires && ['Active', 'Pending'].includes(s.status)) : null
);

export const getSubscriptionInfo = createSelector(
  getActiveSubscription,
  getNextSubscription,
  (current, next = {}) => {
    if (!current) { return {} }
    return {
      paidThroughDate: current.paidThroughDate,
      planId: current.planId,
      nextPlanId: next.planId,
      nextBillingDate: next.nextBillingDate,
      price: next.price,
    }
  },
);

export const getCustomerId = createSelector(
  getCustomers,
  getFirstAccount,
  (customers, account) => {
    return customers && customers[account.key] || null;
  }
);

export const getSubscriptionPlan = createSelector(
  getSubscriptionInfo,
  subscription => subscription && subscription.planId || 'basic',
);

export const isBillingLoading = state => !state.subscriptions.loaded;



// WEBPACK FOOTER //
// ./src/modules/subscriptions/index.js